import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FitnessCenter, History } from "@mui/icons-material";
import { Controller } from "react-hook-form";
import { HistoryModal } from "./HistoryModal";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import {
  extractLymphNodesHistory,
  extractCardiacPulmonaryAuscultationHistory,
  extractEyesMucosaHistory,
  extractOralMucosaHistory,
  extractEarCanalsHistory,
  extractSkinHistory,
  extractLocomotorSystemHistory,
  extractAnteriorLimbsHistory,
  extractPosteriorLimbsHistory,
} from "@/fluxvet/utils/questionnaireResponseHelper";

// =============================================================================
// TIPOS - Interfaces
// =============================================================================

/**
 * Props do componente PhysicalExamSection
 */
interface PhysicalExamSectionProps {
  control: any;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente de Exame Físico
 * Exibe formulário estruturado para registro detalhado do exame físico
 * Inclui botões para visualizar histórico de cada região examinada
 * 
 * Campos incluídos:
 * - Linfonodos (palpação)
 * - Ausculta cardíaca e pulmonar
 * - Olhos e mucosa ocular
 * - Boca e mucosa oral
 * - Condutos auditivos
 * - Pele (descrição + checkboxes de condições)
 * - Aparelho locomotor
 * - Membros anteriores
 * - Membros posteriores
 * 
 * @param control - Control do react-hook-form
 */
export const PhysicalExamSection: React.FC<PhysicalExamSectionProps> = ({
  control,
}) => {
  // Busca o histórico de QuestionnaireResponse do contexto
  const { historyQuestionnaireResponse } = useVetAttendance();

  // Processa o histórico para extrair dados de cada campo
  // Usa useMemo para evitar reprocessamento desnecessário
  const lymphNodesHistory = useMemo(
    () => extractLymphNodesHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const cardiacPulmonaryAuscultationHistory = useMemo(
    () => extractCardiacPulmonaryAuscultationHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const eyesMucosaHistory = useMemo(
    () => extractEyesMucosaHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const oralMucosaHistory = useMemo(
    () => extractOralMucosaHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const earCanalsHistory = useMemo(
    () => extractEarCanalsHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const skinHistory = useMemo(
    () => extractSkinHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const locomotorSystemHistory = useMemo(
    () => extractLocomotorSystemHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const anteriorLimbsHistory = useMemo(
    () => extractAnteriorLimbsHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const posteriorLimbsHistory = useMemo(
    () => extractPosteriorLimbsHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  // Estado único para controlar o modal de histórico
  const [historyModal, setHistoryModal] = useState<{
    isOpen: boolean;
    title: string;
    history: any[];
  }>({
    isOpen: false,
    title: "",
    history: [],
  });

  /**
   * Função auxiliar para abrir o modal de histórico
   */
  const openHistoryModal = (title: string, history: any[]) => {
    setHistoryModal({ isOpen: true, title, history });
  };

  /**
   * Função auxiliar para fechar o modal de histórico
   */
  const closeHistoryModal = () => {
    setHistoryModal({ isOpen: false, title: "", history: [] });
  };

  return (
    <>
      <Box
        sx={{
          maxHeight: "calc(100vh - 400px)",
          overflowY: "auto",
          pr: 1,
        }}
      >
        <Grid container spacing={2}>
          {/* Linfonodos */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FitnessCenter sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Linfonodos (palpação)
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Linfonodos",
                        lymphNodesHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="lymphNodes"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Avaliação dos linfonodos"
                      placeholder="Ex: Linfonodos submandibulares e poplíteos não reativos..."
                      multiline
                      minRows={3}
                      maxRows={8}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Ausculta cardíaca e pulmonar */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FitnessCenter sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Ausculta cardíaca e pulmonar
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Ausculta",
                        cardiacPulmonaryAuscultationHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="cardiacPulmonaryAuscultation"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Ausculta"
                      placeholder="Ex: Ritmo regular, sem sopros. MV presente bilateralmente..."
                      multiline
                      minRows={3}
                      maxRows={8}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Olhos e mucosa ocular */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FitnessCenter sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Olhos e mucosa ocular
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Olhos e Mucosa Ocular",
                        eyesMucosaHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="eyesMucosa"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Avaliação ocular"
                      placeholder="Ex: Mucosas oculares normocoradas e úmidas..."
                      multiline
                      minRows={3}
                      maxRows={8}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Boca e mucosa oral */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FitnessCenter sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Boca e mucosa oral
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Boca e Mucosa Oral",
                        oralMucosaHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="oralMucosa"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Avaliação oral"
                      placeholder="Ex: Mucosas orais normocoradas, dentição em bom estado..."
                      multiline
                      minRows={3}
                      maxRows={8}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Condutos auditivos */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FitnessCenter sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Condutos auditivos
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Condutos Auditivos",
                        earCanalsHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="earCanals"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Avaliação auditiva"
                      placeholder="Ex: Condutos auditivos limpos, sem secreção..."
                      multiline
                      minRows={3}
                      maxRows={8}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Pele - Descrição e condições */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FitnessCenter sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Pele
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal("Histórico - Pele", skinHistory)
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />

                {/* Checkboxes de condições dermatológicas */}
                <Controller
                  name="skinConditions"
                  control={control}
                  render={({ field }) => (
                    <FormGroup row>
                      {[
                        "Alopecia",
                        "Atopia",
                        "Ectoparasitas",
                        "Hiperqueratose",
                        "Prurido",
                        "Pústula",
                        "Seborréia",
                        "Pioderma",
                        "Cisto",
                        "Abcesso",
                      ].map((option) => (
                        <FormControlLabel
                          key={option}
                          sx={{ minWidth: 150 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={field.value?.includes(option) || false}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...(field.value || []), option]
                                  : (field.value || []).filter(
                                      (v) => v !== option
                                    );
                                field.onChange(newValue);
                              }}
                              sx={{
                                color: "#00A884",
                                "&.Mui-checked": { color: "#00A884" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">{option}</Typography>
                          }
                        />
                      ))}
                    </FormGroup>
                  )}
                />

                {/* Campo de texto para descrição geral da pele */}
                <Controller
                  name="skin"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Descrição da pele"
                      placeholder="Ex: Pele íntegra, pelagem brilhante..."
                      multiline
                      minRows={2}
                      maxRows={6}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />

                {/* Campo adicional para outras observações */}
                <Controller
                  name="skinOther"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Outras observações sobre pele/pelagem..."
                      multiline
                      minRows={2}
                      maxRows={6}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Aparelho Locomotor */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FitnessCenter sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Aparelho Locomotor
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Aparelho Locomotor",
                        locomotorSystemHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="locomotorSystem"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Avaliação locomotora"
                      placeholder="Ex: Marcha normal, sem claudicação. Massa muscular preservada..."
                      multiline
                      minRows={3}
                      maxRows={8}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Membros Anteriores */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FitnessCenter sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Membros Anteriores
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Membros Anteriores",
                        anteriorLimbsHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="anteriorLimbs"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Avaliação dos membros anteriores"
                      placeholder="Ex: Membros simétricos, sem edema ou dor à palpação..."
                      multiline
                      minRows={3}
                      maxRows={8}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Membros Posteriores */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FitnessCenter sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Membros Posteriores
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Membros Posteriores",
                        posteriorLimbsHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="posteriorLimbs"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Avaliação dos membros posteriores"
                      placeholder="Ex: Sem alterações, propriocepção preservada..."
                      multiline
                      minRows={3}
                      maxRows={8}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Modal único de histórico */}
      <HistoryModal
        open={historyModal.isOpen}
        onClose={closeHistoryModal}
        title={historyModal.title}
        history={historyModal.history}
      />
    </>
  );
};

export default PhysicalExamSection;

